import { Link, Outlet, useMatch } from 'react-router-dom';
import { useAuth } from '@screentone/addon-auth-wrapper';
import {
  Box,
  Button,
  IconBlocks,
  IconFileText,
  IconFilter,
  IconFlag,
  IconFolder,
  IconGear,
  IconLayoutLeft,
  IconSliders,
  IconSmartphone
} from '@screentone/core';
import { useQueryClient } from '@tanstack/react-query';

import { PageRootContainer } from 'components/page-root-container/PageRootContainer';
import { PageLockProvider } from 'contexts/page-lock/PageLockContext';
import { AvailableFeatureFlags } from 'data/generated/graphql';
import { useConvertedProperty } from 'hooks';
import styles from './styles.module.scss';

function SiteConfig() {
  const { currentPropertyObject } = useAuth();
  const currentProperty = useConvertedProperty();
  const queryClient = useQueryClient();

  const handleRefetch = async () => queryClient.invalidateQueries({ queryKey: ['PublicationSettings'], exact: false });

  const isGeneralActive = !!useMatch({ path: '/:property/site-config', end: true });
  const isModulesActive = !!useMatch('/:property/site-config/modules/*');
  const isLayoutsActive = !!useMatch('/:property/site-config/layouts/*');
  const isPageTypesActive = !!useMatch('/:property/site-config/page-types/*');
  const isQueryRulesActive = !!useMatch('/:property/site-config/query-rules/*');
  const isMobileIssueManagementActive = !!useMatch('/:property/site-config/mobile-and-app/*');
  const isGlobalSettingsActive = !!useMatch('/:property/site-config/global-settings/*');
  const isFeatureFlagsActive = !!useMatch('/:property/site-config/feature-flags/*');
  const isCollectionSettingsActive = !!useMatch('/:property/site-config/collections/*');

  return (
    <PageRootContainer>
      <div className={styles.root}>
        <div className={styles.sidebar}>
          <Box>
            {!!currentPropertyObject && <Box.Title>{`${currentPropertyObject.name} site config`}</Box.Title>}
            <Box.Content padding={{ all: 'md' }}>
              <div className={styles.nav}>
                <Button
                  fullWidth
                  tertiary
                  justified="left"
                  icon={IconGear}
                  className={styles.navButton}
                  componentEl={Link}
                  to=""
                  active={isGeneralActive}
                >
                  Setup & Identity
                </Button>
                <Button
                  fullWidth
                  tertiary
                  justified="left"
                  icon={IconBlocks}
                  className={styles.navButton}
                  componentEl={Link}
                  to="modules"
                  active={isModulesActive}
                >
                  Modules & Treatment Types
                </Button>
                <Button
                  fullWidth
                  tertiary
                  justified="left"
                  icon={IconLayoutLeft}
                  className={styles.navButton}
                  componentEl={Link}
                  to="layouts"
                  active={isLayoutsActive}
                >
                  Pages & Layouts
                </Button>
                <Button
                  fullWidth
                  tertiary
                  justified="left"
                  icon={IconFileText}
                  className={styles.navButton}
                  componentEl={Link}
                  to="page-types"
                  active={isPageTypesActive}
                >
                  Page Types
                </Button>
                <Button
                  fullWidth
                  tertiary
                  justified="left"
                  icon={IconFilter}
                  className={styles.navButton}
                  componentEl={Link}
                  to="query-rules"
                  active={isQueryRulesActive}
                >
                  Query Rules
                </Button>
                <Button
                  fullWidth
                  tertiary
                  justified="left"
                  icon={IconSmartphone}
                  className={styles.navButton}
                  componentEl={Link}
                  to="mobile-and-app"
                  active={isMobileIssueManagementActive}
                >
                  Mobile & App
                </Button>
                <Button
                  fullWidth
                  tertiary
                  justified="left"
                  icon={IconSliders}
                  className={styles.navButton}
                  componentEl={Link}
                  to="global-settings"
                  active={isGlobalSettingsActive}
                >
                  Global
                </Button>
                <Button
                  fullWidth
                  tertiary
                  justified="left"
                  icon={IconFlag}
                  className={styles.navButton}
                  componentEl={Link}
                  to="feature-flags"
                  active={isFeatureFlagsActive}
                >
                  Feature Flags
                </Button>
                <Button
                  fullWidth
                  tertiary
                  justified="left"
                  icon={IconFolder}
                  className={styles.navButton}
                  componentEl={Link}
                  to="collections"
                  active={isCollectionSettingsActive}
                >
                  Collections
                </Button>
              </div>
            </Box.Content>
          </Box>
        </div>
        <div className={styles.main}>
          <PageLockProvider
            allessehId={`publication-settings-${currentProperty ?? ''}`}
            fragments={['publishing']}
            featureFlag={AvailableFeatureFlags.PublicationSettingsLockingEnabled}
            refetcher={handleRefetch}
          >
            <Outlet />
          </PageLockProvider>
        </div>
      </div>
    </PageRootContainer>
  );
}

export default SiteConfig;
